import * as React from "react";
import { GraduationCap, Info, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@eduviaos/ui-kit";
import type { Section, Subject } from "@eduviaos/shared";
import {
  type EligibilityRowDraft,
  isEligibilityRowFilled,
  isEligibilityRowValid,
  newEligibilityDraft,
} from "./teacher-eligibility-helpers";

/**
 * Phase G0 FE — per-teacher eligibility editor embedded inside the
 * invite-staff wizard.
 *
 * The Iron Rule (the user's explicit directive): teachers cannot be
 * invited without subject + grade configuration. This component
 * enforces that at the wire layer by gating the row's "ready" state
 * on at least one valid eligibility entry (subject_id + grade_ref).
 *
 * The "class teacher" toggle requires a section_id — this is enforced
 * at the BE too, but we prevent the IA from saving a row that the BE
 * will refuse with a 422 by greying out the toggle until a section is
 * picked.
 *
 * Section dropdown is filtered by branch + grade live: picking grade
 * "9" only shows sections seeded for grade 9, so a typo can't slip
 * through. If no sections are seeded for a grade, we still allow the
 * teacher to be invited without a section — they just can't be class
 * teacher of an unseeded grade.
 */

interface Props {
  rows: EligibilityRowDraft[];
  onChange: (rows: EligibilityRowDraft[]) => void;
  subjects: Subject[];
  sections: Section[];
  branchId: string | null;
  /** When true, render every row in a more compact way to save vertical
   * space inside an already-busy table cell. */
  compact?: boolean;
}

export function TeacherEligibilityEditor({
  rows,
  onChange,
  subjects,
  sections,
  branchId,
  compact = false,
}: Props) {
  const update = React.useCallback(
    (draftId: string, patch: Partial<EligibilityRowDraft>) => {
      onChange(
        rows.map((row) => {
          if (row.draftId !== draftId) return row;
          const next = { ...row, ...patch };
          // Cross-field invariants:
          //   - grade change resets section because the section list is
          //     filtered by grade; keeping a stale section_id would be
          //     a server-side 422 waiting to happen.
          if (patch.grade_ref !== undefined && patch.grade_ref !== row.grade_ref) {
            next.section_id = "";
            // class-teacher implies section, so clearing section also
            // clears the class-teacher flag rather than leaving a
            // half-formed row staring at the IA.
            if (next.is_class_teacher) next.is_class_teacher = false;
          }
          //   - flipping section to "" must clear class-teacher. The BE
          //     would 422 anyway, but we don't even let the editor reach
          //     that state.
          if (patch.section_id === "" && next.is_class_teacher) {
            next.is_class_teacher = false;
          }
          return next;
        }),
      );
    },
    [onChange, rows],
  );

  const remove = React.useCallback(
    (draftId: string) => {
      // Always keep at least one row visible — the empty row IS the
      // "Add a subject + grade" empty state.
      onChange(
        rows.length === 1 ? [newEligibilityDraft()] : rows.filter((row) => row.draftId !== draftId),
      );
    },
    [onChange, rows],
  );

  const add = React.useCallback(() => {
    onChange([...rows, newEligibilityDraft()]);
  }, [onChange, rows]);

  // Subjects available — exclude archived (defence in depth; the hook
  // already passes `include_archived=false` by default).
  const liveSubjects = subjects.filter((s) => s.archived_at === null);

  return (
    <div className="grid gap-2.5 rounded-md border border-dashed p-3">
      <header className="flex items-start justify-between gap-3">
        <div className="grid gap-0.5">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold">
            <GraduationCap className="size-3.5" aria-hidden />
            Teaching profile
          </p>
          <p className="text-muted-foreground text-[11px]">
            Add the subjects + grades this teacher will handle. The AI timetable solver uses this to
            plan periods. Class teachers are tied to one specific section.
          </p>
        </div>
        <Badge variant={rows.some(isEligibilityRowValid) ? "success" : "warn"}>
          {rows.filter(isEligibilityRowValid).length || 0} valid row
          {rows.filter(isEligibilityRowValid).length === 1 ? "" : "s"}
        </Badge>
      </header>

      <div className="grid gap-2">
        {rows.map((row, idx) => {
          const filtered = sections.filter(
            (sec) =>
              sec.archived_at === null &&
              (branchId ? sec.branch_id === branchId : true) &&
              (row.grade_ref ? sec.grade_ref === row.grade_ref : true),
          );
          const filled = isEligibilityRowFilled(row);
          const invalid = filled && !isEligibilityRowValid(row);
          return (
            <div
              key={row.draftId}
              className={cn(
                "grid gap-2 rounded-md border p-2.5",
                compact
                  ? "sm:grid-cols-[1.4fr_0.8fr_1fr_auto]"
                  : "sm:grid-cols-[1.6fr_1fr_1.2fr_auto] sm:items-end",
                invalid && "border-destructive/40 bg-destructive/5",
              )}
            >
              <div className="grid gap-1">
                <Label htmlFor={`${row.draftId}-subject`} className="text-[11px]">
                  Subject
                </Label>
                <Select
                  value={row.subject_id || undefined}
                  onValueChange={(v) => update(row.draftId, { subject_id: v })}
                >
                  <SelectTrigger id={`${row.draftId}-subject`}>
                    <SelectValue placeholder="Choose subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {liveSubjects.length === 0 ? (
                      <div className="text-muted-foreground px-2 py-1.5 text-xs">
                        No subjects yet — add one in Settings → Academic.
                      </div>
                    ) : (
                      liveSubjects.map((subject) => (
                        <SelectItem key={subject.id} value={subject.id}>
                          {subject.name}
                          <span className="text-muted-foreground ml-2 text-[10px]">
                            {subject.code}
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label htmlFor={`${row.draftId}-grade`} className="text-[11px]">
                  Grade
                </Label>
                <Input
                  id={`${row.draftId}-grade`}
                  placeholder="9"
                  value={row.grade_ref}
                  maxLength={24}
                  onChange={(e) => update(row.draftId, { grade_ref: e.target.value })}
                />
              </div>
              <div className="grid gap-1">
                <Label htmlFor={`${row.draftId}-section`} className="text-[11px]">
                  Section{" "}
                  <span className="text-muted-foreground">
                    {row.is_class_teacher ? "(required)" : "(optional)"}
                  </span>
                </Label>
                <Select
                  value={row.section_id || undefined}
                  onValueChange={(v) => update(row.draftId, { section_id: v === "_none" ? "" : v })}
                  disabled={!row.grade_ref}
                >
                  <SelectTrigger id={`${row.draftId}-section`}>
                    <SelectValue
                      placeholder={
                        !row.grade_ref
                          ? "Pick grade first"
                          : filtered.length === 0
                            ? "Any section"
                            : "Choose section"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Any section</SelectItem>
                    {filtered.length === 0 && row.grade_ref ? (
                      <div className="text-muted-foreground px-2 py-1.5 text-xs">
                        No sections seeded for this grade yet.
                      </div>
                    ) : (
                      filtered.map((section) => (
                        <SelectItem key={section.id} value={section.id}>
                          Section {section.name}
                          <span className="text-muted-foreground ml-2 text-[10px]">
                            {section.grade_ref}
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove eligibility row ${idx + 1}`}
                onClick={() => remove(row.draftId)}
              >
                <Trash2 className="size-4" />
              </Button>

              <div className="sm:col-span-full">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <label
                    className={cn(
                      "inline-flex items-center gap-1.5",
                      !row.section_id && "text-muted-foreground",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={row.is_class_teacher}
                      disabled={!row.section_id}
                      onChange={(e) => update(row.draftId, { is_class_teacher: e.target.checked })}
                    />
                    <span>Class teacher of this section</span>
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={row.preferred}
                      onChange={(e) => update(row.draftId, { preferred: e.target.checked })}
                    />
                    <span>Preferred</span>
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    <span>Max periods/week</span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={60}
                      className="h-7 w-20 text-xs"
                      placeholder="—"
                      value={row.max_periods_per_week_for_this_combo}
                      onChange={(e) =>
                        update(row.draftId, {
                          max_periods_per_week_for_this_combo: e.target.value,
                        })
                      }
                    />
                  </label>
                </div>
                {invalid && (
                  <p className="text-destructive mt-1.5 inline-flex items-center gap-1 text-[11px]">
                    <Info className="size-3" />
                    {row.is_class_teacher && !row.section_id
                      ? "Class teachers are tied to a single section — please pick one."
                      : "Fix the highlighted fields before sending."}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={add} className="self-start">
        <Plus className="size-3.5" />
        Add another subject / grade
      </Button>
    </div>
  );
}
